import { type JanusCtx, type Row, activeStatuses, ec, ecId, ecLean, handBack, record, scalar, upper } from '../context.js';
import { storeMovementPct } from '../../movement/banked-walk.js';
import { statusDefinitionShieldsSpeed } from '../../compile/creature-speed-factor.js';
import { discernVariableTags } from '../discern-variable-tags.js';
import { applyAbilityTeleport } from './ability-teleport.js';

const SPEED_GRANTS = [
  ['change_speed_walking_remaining', 'speed_walking'],
  ['change_speed_jumping_remaining', 'speed_jumping'],
  ['change_speed_climbing_remaining', 'speed_climbing'],
  ['change_speed_flying_remaining', 'speed_flying'],
  ['change_speed_hovering_remaining', 'speed_hovering'],
  ['change_speed_swimming_remaining', 'speed_swimming'],
  ['change_speed_burrowing_remaining', 'speed_burrowing'],
  ['change_teleport_remaining', 'teleport'],
] as const;
export const CASTER_MOVEMENT_GRANT_LAYERS = SPEED_GRANTS.map(([layer]) => layer);
const REASON = 'apply_change_speed_remaining_layers_to_caster';

/** Ordinary speed feet bank a fraction of one AP; a positive teleport earns a separate landing choice. */
export function applyChangeSpeedRemainingLayersToCaster(ctx: JanusCtx, ability: Row, caster: Row): void {
  const grants = SPEED_GRANTS.filter(([layer]) => ctx.read.has(ability, layer));
  if (!grants.length) return;
  const teleported = applyAbilityTeleport(ctx, ability, caster);
  if (!ecId(caster)) return;

  let delta = 0;
  for (const [layer, speed] of grants) {
    if (teleported && layer === 'change_teleport_remaining') continue;
    const raw = scalar(ctx.read.value(ability, layer));
    if (raw == null || raw === '') continue;
    const rawBase = scalar(ctx.read.value(caster, speed));
    const base = rawBase == null ? 0 : Number(rawBase);
    if (Number.isFinite(base) && base <= 0) continue;
    const feet = /^[+-]?\d+$/.test(String(raw).trim()) && Number.isSafeInteger(Number(raw)) ? Number(raw)
      : discernVariableTags(ctx, raw, caster, { reason: REASON, integer: true });
    if (!feet) continue;
    if (!Number.isFinite(base)) handBack(`${REASON}:speed-not-held`);
    delta += feet / base;
  }
  if (!Number.isFinite(delta)) handBack(`${REASON}:invalid-delta`);
  if (Math.abs(delta) <= 1e-9) return;
  // speed_cannot_be_reduced (Wizard v9 "Ever-Changing Form"): while a HELD status carries it, a grant that would SHRINK the banked
  // movement is ignored (a status the page cannot resolve proves no shield)
  if (delta < 0 && casterSpeedShielded(ctx, caster)) return;
  const rawPool = scalar(ecLean(caster, 'movement_pct'));
  if (rawPool == null || rawPool === '' || !Number.isFinite(Number(rawPool)))
    handBack(`${REASON}:pool-not-held`);
  ec(caster).movement_pct = storeMovementPct(Math.max(0, Number(rawPool)) + delta);
}

/** A HELD status (this cast's pending edits included) carries speed_cannot_be_reduced — its own layer or a passive it attaches. */
function casterSpeedShielded(ctx: JanusCtx, caster: Row): boolean {
  for (const status of activeStatuses(ctx, caster)) {
    const def = ctx.read.definition(upper(status.status_item_id)) ?? record(status.status_arr);
    if (!def) continue; // unresolvable: no shield proven — the grant applies as PHP would apply it
    const attached = (d: unknown): unknown[] => ctx.read.entries(d as Row, 'passive_ability')
      .map((e) => { const id = upper(record(e)?.item_id ?? e); return record(e)?.item_layer_types ? e : id ? ctx.read.definition(id) : null; });
    if (statusDefinitionShieldsSpeed(def, ((row: Row, name: string) => ctx.read.value(row, name)) as never, attached)) return true;
  }
  return false;
}
